import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import BarcodeAutocomplete from '@/components/BarcodeAutocomplete';
import { AuthImage } from '@/components/AuthImage';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { uid, money, fmtDate } from '@/lib/helpers';
import { Search, Loader2, Undo2, Repeat, Camera, ImagePlus, ArrowRight } from 'lucide-react';

export default function ReturnsExchanges() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('invoice') || '');
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exchOpen, setExchOpen] = useState(false);
  const [exchItem, setExchItem] = useState(null);
  const [newG, setNewG] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [discount, setDiscount] = useState('');
  const [newImage, setNewImage] = useState('');
  const [origImage, setOrigImage] = useState('');
  const [looking, setLooking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const returnVal = exchItem ? Number(exchItem.final || 0) : 0;
  const priceNum = Number(newPrice || 0);
  const discNum = Math.min(Math.max(Number(discount || 0), 0), priceNum); // clamp 0..newPrice
  const diff = newG ? Math.round((priceNum - discNum - returnVal) * 100) / 100 : 0;

  const openExchange = (item) => {
    setExchItem(item); setNewG(null); setNewPrice(''); setDiscount(''); setNewImage(''); setOrigImage(''); setExchOpen(true);
  };

  const lookupNew = async (barcode) => {
    const bc = String(barcode || '').trim().toUpperCase();
    if (!bc) return;
    setLooking(true);
    try {
      const g = (await api.get(`/garments/lookup/${encodeURIComponent(bc)}`)).data.data;
      if (g.status === 'SOLD') { toast.error(`${bc} is already sold.`); setNewG(null); return; }
      if (g.store_id !== sale.store_id) { toast.error('Replacement must be in the same store as the original bill.'); setNewG(null); return; }
      setNewG(g); setNewPrice(String(g.price));
      const img = g.image || '';
      setNewImage(img); setOrigImage(img);
    } catch (e) { toast.error(e?.response?.status === 404 ? 'Replacement item not found.' : errMsg(e)); setNewG(null); }
    finally { setLooking(false); }
  };

  const changePhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewImage(r.data.data.path);
      toast.success('Photo updated');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  };

  const load = async (invoiceNo) => {
    const inv = (invoiceNo || q).trim();
    if (!inv) return;
    setLoading(true); setSale(null);
    try {
      // try direct invoice; fallback to search
      let s = null;
      try { s = (await api.get(`/sales/${inv}`)).data.data; }
      catch { const r = await api.get('/sales', { params: { q: inv } }); s = r.data.data[0]; }
      if (!s) { toast.error('No sale found for that search.'); }
      setSale(s);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get('invoice')) load(params.get('invoice')); /* eslint-disable-next-line */ }, []);

  const doReturn = async (barcode) => {
    if (!window.confirm('Return this garment? It will be added back to available stock.')) return;
    setBusy(true);
    try {
      const r = await api.post('/returns', { invoice_no: sale.invoice_no, barcode, client_txn_id: uid() });
      toast.success(`Returned · refund ${money(r.data.data.refund)}`);
      load(sale.invoice_no);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doExchange = async () => {
    if (!newG) { toast.error('Pick a replacement garment.'); return; }
    setBusy(true);
    try {
      const patch = {};
      if (priceNum && priceNum !== Number(newG.price)) patch.price = priceNum;
      if (newImage !== origImage) patch.image = newImage;
      if (Object.keys(patch).length) await api.patch(`/garments/${encodeURIComponent(newG.barcode)}`, patch);
      const r = await api.post('/exchanges', { invoice_no: sale.invoice_no, return_barcode: exchItem.barcode, new_barcode: newG.barcode, discount: discNum || 0, client_txn_id: uid() });
      const d = r.data.data;
      toast.success(d.difference > 0 ? `Exchange done · collect ${money(d.customer_pays)}` : d.difference < 0 ? `Exchange done · refund ${money(d.refund_due)}` : 'Exchange done · no difference');
      setExchOpen(false); load(sale.invoice_no);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Page>
      <TopBar title="Return / Exchange" back showStore={false} />
      <div className="px-4 pt-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="returns-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Invoice no, barcode or mobile" className="h-11 rounded-xl pl-9" />
          </div>
          <Button className="h-11 rounded-xl" onClick={() => load()} data-testid="returns-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}</Button>
        </div>

        {sale && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="returns-sale">
            <div className="flex items-center justify-between">
              <div><p className="font-mono text-sm font-semibold">{sale.invoice_no}</p><p className="text-xs text-muted-foreground">{fmtDate(sale.created_at)} · {sale.store_name}</p></div>
              <p className="font-mono text-sm font-semibold">{money(sale.net)}</p>
            </div>
            <div className="mt-3 space-y-2 border-t border-border pt-3">
              {sale.items.map((i) => (
                <div key={i.barcode} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`truncate text-sm font-medium ${i.returned ? 'text-muted-foreground line-through' : ''}`}>{i.product}</p>
                    <p className="font-mono text-xs text-muted-foreground">{i.barcode} · {money(i.final)}</p>
                  </div>
                  {i.returned ? <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">Returned</span> : (
                    <div className="flex gap-1">
                      <Button size="sm" variant="secondary" className="h-8 rounded-lg" disabled={busy} onClick={() => doReturn(i.barcode)} data-testid={`return-item-${i.barcode}`}><Undo2 className="mr-1 h-3 w-3" />Return</Button>
                      <Button size="sm" variant="secondary" className="h-8 rounded-lg" onClick={() => openExchange(i)} data-testid={`exchange-item-${i.barcode}`}><Repeat className="mr-1 h-3 w-3" />Exchange</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Drawer open={exchOpen} onOpenChange={setExchOpen}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>Exchange garment</DrawerTitle></DrawerHeader>
          <div className="max-h-[75vh] space-y-3 overflow-y-auto px-4 pb-6">
            <Label className="block text-xs">Replacement item</Label>
            <BarcodeAutocomplete storeId={sale?.store_id} statusFilter="AVAILABLE" testId="exchange-new-barcode"
              placeholder="Search or scan replacement" onSelect={(g) => lookupNew(g.barcode)} onSubmit={(code) => lookupNew(code)} />
            {looking && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}

            {newG && (
              <>
                {/* Photo of the replacement item — changeable */}
                <div className="flex items-center gap-3 rounded-xl border border-border p-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary">
                    {newImage ? <AuthImage path={newImage} alt="Item" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Camera className="h-5 w-5" /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{newG.product || newG.barcode}</p>
                    <p className="truncate font-mono text-xs text-muted-foreground">{newG.barcode}</p>
                    <label className="mt-1 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary">
                      <ImagePlus className="h-3.5 w-3.5" />{uploading ? 'Uploading…' : (newImage ? 'Change photo' : 'Add photo')}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => changePhoto(e.target.files?.[0])} data-testid="exchange-photo-input" />
                    </label>
                  </div>
                </div>

                {/* Editable price */}
                <div>
                  <Label className="text-xs">New item price</Label>
                  <Input inputMode="numeric" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="exchange-new-price" />
                </div>
                <div>
                  <Label className="text-xs">Discount on new item <span className="text-muted-foreground">(optional)</span></Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
                    <Input inputMode="numeric" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" className="h-11 rounded-xl pl-7" data-testid="exchange-discount" />
                  </div>
                </div>

                {/* Invoice-style difference summary */}
                <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="exchange-summary">
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">Returning · {exchItem?.product}</span><span className="font-mono text-rose-600">− {money(returnVal)}</span></div>
                  <div className="mt-1 flex items-center justify-between"><span className="text-muted-foreground">New · {newG.product}</span><span className="font-mono text-emerald-600">+ {money(priceNum)}</span></div>
                  {discNum > 0 && (
                    <div className="mt-1 flex items-center justify-between"><span className="text-muted-foreground">Discount</span><span className="font-mono text-rose-600" data-testid="exchange-discount-line">− {money(discNum)}</span></div>
                  )}
                  <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-base font-semibold">
                    <span className="flex items-center gap-1">{diff >= 0 ? 'Customer pays' : 'Refund due'} <ArrowRight className="h-3.5 w-3.5" /></span>
                    <span className="font-mono" data-testid="exchange-difference">{money(Math.abs(diff))}</span>
                  </div>
                </div>
              </>
            )}

            <Button className="mt-1 h-12 w-full rounded-xl font-semibold" onClick={doExchange} disabled={busy || !newG} data-testid="exchange-confirm">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Confirm exchange'}</Button>
          </div>
        </DrawerContent>
      </Drawer>
    </Page>
  );
}
